import React from "react";
import chevronDown from "../../../assets/Chevron/ChevronDown.svg";

export type ButtonPulldownProps = {
  label?: string; // プレースホルダー（未選択時に表示）
  options?: string[]; // 選択肢
  value?: string; // 制御用の選択値（未指定時は未選択）
  onChange?: (
    next: string,
  ) => void; // 選択時コールバック
  onBlur?: () => void;
  disabled?: boolean;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
  ariaDescribedBy?: string;
  className?: string;
  containerClassName?: string; // 内側コンテナのサイズ等を上書きするため
  maxWidth?: number; // select 部分の最大幅（px）。未指定時は制限なし
};

export function ButtonPulldown({
  label = "カテゴリ",
  options = [],
  value,
  onChange,
  onBlur,
  disabled = false,
  ariaInvalid = false,
  ariaRequired = false,
  ariaDescribedBy,
  className,
  containerClassName,
  maxWidth,
}: ButtonPulldownProps) {
  // ドロップダウン開閉改善: コンテナ全体（右アイコン含む）クリックで
  // ネイティブ select を開く。Chrome系は showPicker()、非対応は
  // focus()→click() フォールバックを実施（アクセシビリティ配慮）。
  const selectRef = React.useRef<HTMLSelectElement | null>(null);
  const [selectWidth, setSelectWidth] = React.useState<number | undefined>(
    undefined,
  );

  // options が毎回新しい配列参照でも、内容が同じなら幅再計算を避ける
  const optionsSignature = React.useMemo(
    () => options.join("\u001f"),
    [options],
  );

  React.useEffect(() => {
    const el = selectRef.current;
    if (!el) return;

    const computedFont = window.getComputedStyle(el).font;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.font = computedFont;
    const allTexts = [label, ...options];
    const measured = Math.ceil(
      Math.max(...allTexts.map((t) => ctx.measureText(t).width)),
    );
    const nextWidth =
      maxWidth !== undefined ? Math.min(maxWidth, measured) : measured;
    setSelectWidth((prev) => (prev === nextWidth ? prev : nextWidth));
  }, [label, optionsSignature, maxWidth]);
  const containerClass =
    `bg-white border border-menuBorder rounded-pill ` +
    `px-s-400 py-s-200 inline-flex items-center justify-between gap-s-200 ` +
    `h-btnSm`;

  const isEmpty = !value || value === "";

  return (
    <div
      className={`relative inline-block` + (className ? ` ${className}` : "")}
    >
      <div
        data-name="Btn_Pulldown"
        data-node-id="1296:8703"
        className={
          containerClass + (containerClassName ? ` ${containerClassName}` : "")
        }
        // コンテナクリックでセレクトを開く（右アイコン含む領域全体）
        onClick={(e) => {
          if (disabled) return;
          // select 要素自体のクリックはネイティブ動作に委ねる
          // （Windows Chrome/Edge でバブリングにより showPicker が再発火し
          //   プルダウンが閉じない問題を防止）
          if (e.target === selectRef.current) return;
          const el = selectRef.current;
          if (!el) return;
          // Chrome系: showPicker があれば優先使用
          if (typeof (el as any).showPicker === "function") {
            try {
              (el as any).showPicker();
              return;
            } catch (_) {
              // 非対応や例外時はフォールバックへ
            }
          }
          // フォールバック: フォーカスしてクリックを発火
          try {
            el.focus();
            el.click();
          } catch (_) {
            // 失敗時は何もしない（ネイティブ動作に委ねる）
          }
        }}
      >
        <select
          ref={selectRef}
          disabled={disabled}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          style={selectWidth !== undefined ? { width: selectWidth } : undefined}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          aria-describedby={ariaDescribedBy}
          className={
            `bg-transparent text-[14px] leading-body overflow-hidden text-ellipsis whitespace-nowrap ` +
            `focus:outline-none appearance-none border-none m-0 p-0 ` +
            (disabled ? `cursor-not-allowed ` : ``) +
            (isEmpty ? `text-borderGray` : `text-textDefault`)
          }
          aria-label={label}
        >
          <option value="">{label}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <img
          src={chevronDown}
          alt=""
          className="w-4 h-4 shrink-0 pointer-events-none"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

export default ButtonPulldown;
